import { collection, doc, getDocs, getDocFromServer, query, where, runTransaction, serverTimestamp } from 'firebase/firestore';
import db, { auth } from '../firebaseConfig';
import { getAuthorizedUserRole } from './authConfig';
import { COUNTRY_CONFIG, getCollectionName, VALID_COUNTRY_IDS } from './countryConfig';
import { getProductVariants, withStableVariants } from './productVariants';
import { assertProductSize, cleanProductPatch, sameValue } from './productModel';
import { rebaseProductPatch } from './productDirtyPatch';
import { readProductIdentities, readWithDeadline } from './productIdentityRead';

export const requireProductActor = async () => {
  const user = auth.currentUser;
  if (!user || !await getAuthorizedUserRole(user) || auth.currentUser?.uid !== user.uid) throw new Error('請先以現有管理員或工作人員帳號登入');
  return user.uid;
};
const checkCountry = country => {
  if (!VALID_COUNTRY_IDS.includes(country)) throw new Error('無效的國家');
};
export const findProductsByCode = async (country, value, { preserveWhitespace = false } = {}) => {
  checkCountry(country);
  const code = preserveWhitespace ? String(value ?? '') : String(value ?? '').trim();
  if (!code) return [];
  const ref = collection(db, getCollectionName(country));
  const results = await Promise.all([
    getDocs(query(ref, where('itemNumber', '==', code))),
    getDocs(query(ref, where('barcode', '==', code))),
    getDocs(query(ref, where('rawBarcode', '==', code))),
    getDocs(query(ref, where('rawStyleBarcodes', 'array-contains', code))),
    getDocs(query(ref, where('styleBarcodes', 'array-contains', code))),
    // Old records may store only barcode+itemNumber. Until raw barcode backfill is complete,
    // an exact client-side legacy fallback is required to avoid creating duplicate mothers.
    getDocs(ref),
  ]);
  const found = new Map();
  results.forEach(s => s.docs.forEach(d => {
    const product = { ...d.data(), id: d.id };
    if (String(product.itemNumber ?? '') === code || String(product.barcode ?? '') === code || String(product.rawBarcode ?? '') === code
      || getProductVariants(product).some(v => v.barcode === code || v.rawBarcode === code)) found.set(d.id, product);
  }));
  return [...found.values()];
};
export const keysFor = product => [...new Set([
  String(product.itemNumber ?? '').trim() ? `code:${String(product.itemNumber).trim()}` : '',
  ...getProductVariants(product).flatMap(v => [v.rawBarcode && `code:${v.rawBarcode}`, v.barcode && `code:${v.barcode}`]),
  String(product.barcode ?? '').trim() ? `code:${String(product.barcode).trim()}` : '',
  String(product.rawBarcode ?? '').trim() ? `code:${String(product.rawBarcode).trim()}` : '',
].filter(Boolean))];
const claimRef = (country, key) => {
  const encoded = Array.from(`${country}:${key}`).map(c => c.codePointAt(0).toString(16)).join('-');
  if (encoded.length > 1400) throw new Error('料號或條碼過長');
  return doc(db, 'productIdentityClaims', encoded);
};
const duplicateError = matches => Object.assign(new Error('此料號或條碼已存在，請選擇現有商品；目前輸入仍保留。'), { code: 'product-duplicate', matches });
const assertNoLegacyDuplicates = async (country, product, id, readIdentities) => {
  const codes = [...new Set(keysFor(product).map(key => key.slice(key.indexOf(':') + 1)))];
  if (codes.length > 180) throw new Error('單次商品最多支援 180 個識別碼，請拆分後再儲存');
  const matches = new Map();
  // One full legacy read per commit, not one catalogue read for every variant.
  const candidates = readIdentities ? await readIdentities()
    : (await getDocs(collection(db, getCollectionName(country)))).docs.map(snapshot => ({ ...snapshot.data(), id: snapshot.id }));
  const wanted = new Set(codes);
  candidates.forEach(candidate => {
    const candidateCodes = keysFor(candidate).map(key => key.slice(key.indexOf(':') + 1));
    if (candidate.id !== id && candidateCodes.some(code => wanted.has(code))) matches.set(candidate.id, candidate);
  });
  if (matches.size) throw duplicateError([...matches.values()]);
};

export const createProductSafely = async ({ country, product, id, persistVariantIds = true, onProgress, onTiming }) => {
  checkCountry(country);
  const notify = (callback, value) => { try { callback?.(value); } catch (_) { /* Diagnostic/UI callbacks cannot affect writes. */ } };
  const stage = async (phase, work) => {
    notify(onProgress, phase);
    const start = Date.now();
    try { return await work(); } finally { notify(onTiming, { phase, durationMs: Date.now() - start }); }
  };
  const actor = await stage('authorization', () => readWithDeadline(() => requireProductActor()));
  const ref = id ? doc(db, getCollectionName(country), id) : doc(collection(db, getCollectionName(country)));
  const initial = { ...cleanProductPatch(product), ...(Array.isArray(product.history) ? { history: product.history } : {}) };
  const data = persistVariantIds ? withStableVariants(initial) : initial;
  assertProductSize(data);
  const recover = saved => {
    const comparable = (key, value) => key === 'photos' && Array.isArray(value)
      ? value.map(({ createdAt, ...photo }) => photo) : value;
    const changed = Object.keys(data).filter(key => !sameValue(comparable(key, data[key]), comparable(key, saved[key])));
    if (changed.length) throw Object.assign(new Error('上次儲存已成功，但目前輸入與已存商品不同。請先查看已存商品；目前輸入仍保留。'), { code: 'product-recovered', current: { ...saved, id: ref.id }, fields: changed });
    return { ...saved, id: ref.id };
  };
  // Confirm a previous uncertain success before downloading any catalogue. Still
  // recheck inside the transaction to handle another in-flight attempt safely.
  const previous = await stage('previous-save', () => readWithDeadline(() => getDocFromServer(ref)));
  if (previous.exists()) return recover(previous.data());
  const identities = await stage('identity-read', () => readProductIdentities(country, actor));
  await stage('identity-check', () => assertNoLegacyDuplicates(country, data, ref.id, async () => identities));
  const keys = keysFor(data);
  if (auth.currentUser?.uid !== actor) throw new Error('登入狀態已變更，請重新登入');
  return stage('transaction', () => runTransaction(db, async transaction => {
    const existing = await transaction.get(ref);
    if (existing.exists()) {
      return recover(existing.data());
    } // Exact retry is idempotent, changed retry never silently drops edits.
    const claims = await stage('identity-claims', () => Promise.all(keys.map(key => transaction.get(claimRef(country, key)))));
    const duplicate = claims.find(s => s.exists() && s.data().productId !== ref.id);
    if (duplicate) throw duplicateError([{ id: duplicate.data().productId }]);
    notify(onProgress, 'commit');
    keys.forEach(key => transaction.set(claimRef(country, key), { country, key, productId: ref.id, updatedAt: serverTimestamp(), updatedBy: actor }));
    transaction.set(ref, { ...data, revision: 1, createdAt: serverTimestamp(), createdBy: actor, updatedAt: serverTimestamp(), updatedBy: actor });
    return { ...data, id: ref.id, revision: 1 };
  }));
};

export const saveProductPatch = async ({ country, id, base, patch, guardFields = [] }) => {
  checkCountry(country);
  const actor = await requireProductActor();
  const supplied = cleanProductPatch(patch);
  const ref = doc(db, getCollectionName(country), id);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error('商品已不存在，未儲存變更');
    const current = snap.data();
    if (current.country !== undefined && current.country !== country) throw new Error('商品國家不符');
    if (auth.currentUser?.uid !== actor) throw new Error('登入狀態已變更，請重新登入');
    // Import lookups must still refer to the same identity, not another item.
    const fields = guardFields.filter(key => !sameValue(base[key], current[key]));
    if (fields.length) throw Object.assign(new Error('商品識別碼配對已變更，請重新配對商品'), { code: 'product-identity-changed', fields });
    const cleaned = rebaseProductPatch(base, current, supplied);
    if (!Object.keys(cleaned).length) return { ...current, id };
    const next = { ...current, ...cleaned };
    // Persist legacy identities only when the variants are actually touched, or are unchanged since read.
    const stable = withStableVariants(next);
    const identityChanged = !sameValue(keysFor(current).sort(), keysFor(stable).sort());
    if (identityChanged) await assertNoLegacyDuplicates(country, stable, id);
    const keys = identityChanged ? keysFor(stable) : [];
    const oldKeys = identityChanged ? keysFor(current) : [];
    const union = [...new Set([...keys, ...oldKeys])];
    if (union.length > 360) throw new Error('識別碼數量過多，請分次修改');
    const claims = await Promise.all(union.map(key => transaction.get(claimRef(country, key))));
    const snapshots = new Map(union.map((key, i) => [key, claims[i]]));
    const duplicate = keys.find(key => snapshots.get(key).exists() && snapshots.get(key).data().productId !== id);
    if (duplicate) throw duplicateError([{ id: snapshots.get(duplicate).data().productId }]);
    assertProductSize(stable);
    oldKeys.filter(key => !keys.includes(key)).forEach(key => {
      if (snapshots.get(key).data()?.productId === id) transaction.delete(claimRef(country, key));
    });
    keys.forEach(key => transaction.set(claimRef(country, key), { country, key, productId: id, updatedAt: serverTimestamp(), updatedBy: actor }));
    const revision = (Number(current.revision) || 0) + 1;
    transaction.update(ref, { ...cleaned, styleIds: stable.styleIds, revision, updatedAt: serverTimestamp(), updatedBy: actor });
    const auditRef = doc(collection(ref, 'changes'));
    transaction.set(auditRef, { fields: Object.keys(cleaned), revision, actor, at: serverTimestamp() });
    return { ...stable, revision, id };
  });
};

export const appendProductPhoto = async ({ country, id, photo }) => {
  const actor = await requireProductActor();
  const ref = doc(db, getCollectionName(country), id);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error('商品已不存在');
    const product = snap.data();
    const photos = product.photos || [];
    if (photos.some(p => p.id === photo.id || p.url === photo.url)) return { ...product, id };
    const next = { ...product, photos: [...photos, { ...photo, isCover: false, source: 'quick-intake' }] };
    assertProductSize(next);
    transaction.update(ref, { photos: next.photos, styleIds: getProductVariants(product).map(v => v.id), revision: (Number(product.revision) || 0) + 1, updatedAt: serverTimestamp(), updatedBy: actor });
    return { ...next, id };
  });
};

// Compatibility boundary for the old batch tools. A prep-enabled product must never
// be full-replaced/deleted by a writer that does not understand stable references.
export const isPrepProtectedProduct = product => Object.prototype.hasOwnProperty.call(product || {}, 'styleIds')
  || Object.prototype.hasOwnProperty.call(product || {}, 'dataStatus')
  || Object.prototype.hasOwnProperty.call(product || {}, 'effects');
const assertLegacyWritable = product => {
  if (isPrepProtectedProduct(product)) throw Object.assign(new Error('此商品已使用直播備貨／功效／樣品資料，舊版批次覆寫、刪除或合併已停用，避免款式錯位。請到商品編輯頁修改或停用款式。'), { code: 'legacy-product-protected' });
};
const countryForRef = ref => {
  const name = ref.path.split('/')[0];
  const config = Object.values(COUNTRY_CONFIG).find(c => c.collection === name);
  if (!config) throw new Error('此相容性保護只適用商品集合');
  return config.id;
};
export const writeLegacyProductSafely = async (ref, data, base) => {
  const country = countryForRef(ref);
  const actor = await requireProductActor();
  const supplied = cleanProductPatch(data);
  const result = await runTransaction(db, async tx => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) return null;
    const current = snapshot.data();
    assertLegacyWritable(current);
    if (current.country !== undefined && current.country !== country) throw new Error('商品國家不符');
    const candidate = rebaseProductPatch(base || {}, current, supplied);
    // Old batch records intentionally remain legacy-shaped until the editor
    // explicitly assigns stable IDs; unrelated remote values are kept.
    if (!current.styleIds && !data.styleIds) delete candidate.styleIds;
    const merged = { ...current, ...candidate };
    await assertNoLegacyDuplicates(country, merged, ref.id);
    const nextKeys = keysFor(merged);
    const oldKeys = keysFor(current);
    const allKeys = [...new Set([...oldKeys, ...nextKeys])];
    if (allKeys.length > 360) throw new Error('識別碼數量過多，請分次修改');
    const claims = await Promise.all(allKeys.map(key => tx.get(claimRef(country, key))));
    const claimMap = new Map(allKeys.map((key, index) => [key, claims[index]]));
    const duplicate = nextKeys.find(key => claimMap.get(key).exists() && claimMap.get(key).data().productId !== ref.id);
    if (duplicate) throw duplicateError([{ id: claimMap.get(duplicate).data().productId }]);
    oldKeys.filter(key => !nextKeys.includes(key)).forEach(key => {
      if (claimMap.get(key).data()?.productId === ref.id) tx.delete(claimRef(country, key));
    });
    nextKeys.forEach(key => tx.set(claimRef(country, key), { country, key, productId: ref.id, updatedAt: serverTimestamp(), updatedBy: actor }));
    const revision = (Number(current.revision) || 0) + 1;
    const appendedHistory = (data.history || []).filter(record => !(base?.history || []).some(old => sameValue(old, record)) && !(current.history || []).some(old => sameValue(old, record)));
    const next = { ...merged, history: [...(current.history || []), ...appendedHistory], revision, createdAt: current.createdAt || serverTimestamp(), createdBy: current.createdBy || actor, updatedAt: serverTimestamp(), updatedBy: actor };
    assertProductSize(next);
    tx.set(ref, next);
    return { ...next, id: ref.id };
  });
  if (result) return result;
  return createProductSafely({ country, id: ref.id, product: data, persistVariantIds: false });
};
export const deleteLegacyProductsSafely = async refs => {
  if (!Array.isArray(refs) || !refs.length || refs.length > 100) throw new Error('單次刪除須為 1–100 個舊商品');
  refs.forEach(countryForRef);
  await requireProductActor();
  return runTransaction(db, async tx => {
    const snapshots = await Promise.all(refs.map(ref => tx.get(ref)));
    snapshots.forEach(snapshot => { if (snapshot.exists()) assertLegacyWritable(snapshot.data()); });
    const claimsToRead = snapshots.flatMap((snapshot, index) => snapshot.exists()
      ? keysFor(snapshot.data()).map(key => ({ key, country: countryForRef(refs[index]), productId: refs[index].id })) : []);
    if (claimsToRead.length + refs.length > 400) throw new Error('此次刪除涉及太多識別碼，請減少商品數量');
    const claims = await Promise.all(claimsToRead.map(entry => tx.get(claimRef(entry.country, entry.key))));
    claimsToRead.forEach((entry, index) => { if (claims[index].data()?.productId === entry.productId) tx.delete(claimRef(entry.country, entry.key)); });
    refs.forEach((ref, index) => { if (snapshots[index].exists()) tx.delete(ref); });
    return { deletedCount: snapshots.filter(snapshot => snapshot.exists()).length };
  });
};
export const deleteLegacyProductSafely = ref => deleteLegacyProductsSafely([ref]);
export const mergeLegacyProductsSafely = async ({ country, mainProduct, candidates, product }) => {
  const actor = await requireProductActor();
  const source = [mainProduct, ...candidates];
  if (source.length > 100) throw new Error('單次合併最多 100 個舊商品');
  const refs = source.map(p => doc(db, getCollectionName(country), p.id));
  return runTransaction(db, async tx => {
    const snapshots = await Promise.all(refs.map(ref => tx.get(ref)));
    snapshots.forEach((snapshot, index) => {
      if (!snapshot.exists()) throw new Error('合併來源已變更，請重新預覽');
      const current = snapshot.data();
      assertLegacyWritable(current);
      const { id, ...base } = source[index];
      if (!sameValue(current, base)) throw new Error('合併來源已有新修改，請重新預覽；尚未刪除任何來源');
    });
    const oldKeys = [...new Set(snapshots.flatMap(snapshot => keysFor(snapshot.data())))];
    const nextKeys = keysFor(product);
    const allKeys = [...new Set([...oldKeys, ...nextKeys])];
    if (allKeys.length + refs.length > 350) throw new Error('合併涉及太多識別碼，請減少商品數量');
    const sourceIds = new Set(source.map(p => p.id));
    const claims = await Promise.all(allKeys.map(key => tx.get(claimRef(country, key))));
    const claimMap = new Map(allKeys.map((key, index) => [key, claims[index]]));
    if (nextKeys.some(key => claimMap.get(key).exists() && !sourceIds.has(claimMap.get(key).data().productId))) throw new Error('合併識別碼已被其他商品使用，請重新預覽');
    oldKeys.filter(key => !nextKeys.includes(key)).forEach(key => { if (sourceIds.has(claimMap.get(key).data()?.productId)) tx.delete(claimRef(country, key)); });
    nextKeys.forEach(key => tx.set(claimRef(country, key), { country, key, productId: mainProduct.id, updatedBy: actor, updatedAt: serverTimestamp() }));
    refs.slice(1).forEach(ref => tx.delete(ref));
    tx.set(refs[0], { ...product, revision: (Number(snapshots[0].data().revision) || 0) + 1, updatedBy: actor, updatedAt: serverTimestamp() });
  });
};
